import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { listFailedMappings } from "../api/adminApi";
import { ShortId, fmtDate, Section } from "../components/admin/AdminUI";

function Row({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-zinc-900/60 hover:bg-zinc-900/30 cursor-pointer"
          onClick={() => setOpen((v) => !v)}>
        <td className="px-3 py-2"><ShortId value={item.ulid} /></td>
        <td className="px-3 py-2 text-zinc-500">{fmtDate(item.created_at)}</td>
        <td className="px-3 py-2 text-zinc-400">{item.source}</td>
        <td className="px-3 py-2 text-zinc-400">{item.vertical}</td>
        <td className="px-3 py-2">
          <span className="px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 text-[10px] uppercase tracking-wider">
            {item.error_code}
          </span>
        </td>
        <td className="px-3 py-2 max-w-md truncate" title={item.error_message}>
          {item.error_message}
        </td>
        <td className="px-3 py-2">
          {item.raw_listing_ulid ? (
            <Link to={`/admin/listings?q=${item.raw_listing_ulid}`} className="text-emerald-300 hover:underline">
              <ShortId value={item.raw_listing_ulid} />
            </Link>
          ) : "—"}
        </td>
        <td className="px-3 py-2">
          {item.import_run_ulid ? (
            <Link to={`/admin/imports/${item.import_run_ulid}`} className="text-emerald-300 hover:underline">
              <ShortId value={item.import_run_ulid} />
            </Link>
          ) : "—"}
        </td>
        <td className="px-3 py-2 text-zinc-500 text-center">{open ? "▾" : "▸"}</td>
      </tr>
      {open && (
        <tr className="bg-zinc-900/40">
          <td colSpan={9} className="p-3">
            <div className="rounded border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                payload_excerpt
              </div>
              <pre className="text-[11px] font-mono text-zinc-300 whitespace-pre-wrap break-all max-h-96 overflow-auto">
                {JSON.stringify(item.payload_excerpt, null, 2)}
              </pre>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminFailedMappingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [errorCode, setErrorCode] = useState(searchParams.get("error_code") || "");
  const [importRunUlid, setImportRunUlid] = useState(
    searchParams.get("import_run_ulid") || "",
  );

  React.useEffect(() => {
    const sp = new URLSearchParams();
    if (errorCode) sp.set("error_code", errorCode);
    if (importRunUlid) sp.set("import_run_ulid", importRunUlid);
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode, importRunUlid]);

  const {
    data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading,
  } = useInfiniteQuery({
    queryKey: ["admin", "failed-mappings", errorCode, importRunUlid],
    queryFn: ({ pageParam }) => listFailedMappings({
      limit: 50,
      cursor: pageParam,
      ...(errorCode ? { error_code: errorCode } : {}),
      ...(importRunUlid ? { import_run_ulid: importRunUlid } : {}),
    }),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor || undefined,
  });

  const rows = useMemo(
    () => (data?.pages || []).flatMap((p) => p.items || []),
    [data],
  );

  // Build the error_code filter options from loaded rows (a small client-side aggregation).
  const codeOptions = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.error_code) set.add(r.error_code);
    return Array.from(set).sort();
  }, [rows]);

  return (
    <div data-testid="admin-failed-mappings-page" className="space-y-6">
      <header>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          /api/admin/failed-mappings
        </span>
        <h1 className="text-2xl text-zinc-100 font-medium mt-1">Failed mappings</h1>
      </header>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/50 p-4 flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-zinc-500">Error code</span>
          <select
            data-testid="failed-mappings-error-code"
            value={errorCode}
            onChange={(e) => setErrorCode(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-zinc-200 min-w-40"
          >
            <option value="">any</option>
            {codeOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs flex-1 min-w-[260px]">
          <span className="text-zinc-500">Import run ULID</span>
          <input
            type="text"
            data-testid="failed-mappings-import-run-ulid"
            value={importRunUlid}
            onChange={(e) => setImportRunUlid(e.target.value.trim())}
            placeholder="01J…"
            className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-zinc-200 font-mono text-xs"
          />
        </label>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="failed-mappings-empty"
          className="text-sm text-zinc-500 border border-zinc-800 rounded-xl p-6 text-center"
        >
          Nothing failed. Healthy pipeline ✨
        </div>
      ) : (
        <Section title={`Records · ${rows.length} loaded`}>
          <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/50">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">ULID</th>
                  <th className="text-left px-3 py-2">Created</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Vert</th>
                  <th className="text-left px-3 py-2">Error code</th>
                  <th className="text-left px-3 py-2">Message</th>
                  <th className="text-left px-3 py-2">Raw ulid</th>
                  <th className="text-left px-3 py-2">Run</th>
                  <th className="text-center px-3 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                {rows.map((r) => <Row key={r.ulid} item={r} />)}
              </tbody>
            </table>
          </div>
          {hasNextPage && (
            <div className="text-center pt-3">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                data-testid="failed-mappings-load-more"
                className="px-4 py-1.5 rounded-md border border-zinc-700 text-zinc-200 text-sm hover:border-emerald-500/60 disabled:opacity-40"
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
